import { type IconType, Pill } from "@clipboard-health/ui-components";
import { Skeleton } from "@mui/material";
import { type SxProps, type Theme, useTheme } from "@mui/material/styles";

import {
  type PayoutMethod,
  type PayoutStatus,
  type ShiftPaymentStatus,
} from "../../api/useGetShiftPaymentStatus";

interface PaymentStatusPillProps {
  /**
   * Payment status data from the payments API.
   * If undefined, shows a fallback based on verification status.
   */
  readonly paymentStatus: ShiftPaymentStatus | undefined;
  /**
   * Whether the shift is verified (timesheet approved).
   * Used as fallback when payment status is not available.
   */
  readonly isVerified: boolean;
  /**
   * Whether the shift uses InstantPay.
   * Used as fallback when payment status is not available.
   */
  readonly isInstantPay: boolean;
  /**
   * Whether the payment status is currently loading.
   */
  readonly isLoading?: boolean;
}

/**
 * Payment status pill component for the Support shift details page.
 * Shows payment status from the API when available, with fallback
 * to verification-based status when payment data is unavailable.
 */
export function PaymentStatusPill(props: PaymentStatusPillProps) {
  const { paymentStatus, isVerified, isInstantPay, isLoading = false } = props;
  const theme = useTheme();

  if (isLoading) {
    return <Skeleton variant="rounded" width={60} height={24} />;
  }

  // Determine effective status from payment data or verification fallback
  const effectiveStatus = getEffectiveStatus(paymentStatus, isVerified);
  const effectiveMethod = getEffectiveMethod(paymentStatus, isInstantPay);
  const icon = getIcon(theme, effectiveStatus, effectiveMethod);

  return (
    <Pill
      variant="filled"
      size="small"
      backgroundColor={(theme) => getPillBackgroundColor(theme, effectiveStatus)}
      color={(theme) => getPillTextColor(theme, effectiveStatus)}
      label={getPillText(effectiveStatus)}
      iconType={icon?.type}
      iconColor={(_) => icon?.color}
      sx={(theme) => ({
        height: theme.spacing(6),
        minHeight: theme.spacing(6),
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
        "& .MuiSvgIcon-root": icon?.style,
      })}
    />
  );
}

/**
 * Effective status includes API statuses plus fallbacks.
 * Matches the Earnings page's status display logic.
 */
type EffectiveStatus = PayoutStatus | "pending-verification" | "reversal";

/**
 * Detects if a payment is a reversal (negative amount).
 * Matches the Earnings page's isReversalPaymentItem logic.
 */
function isReversal(paymentStatus: ShiftPaymentStatus): boolean {
  return (paymentStatus.amountInCents ?? 0) < 0;
}

/**
 * Gets the effective payment status from the payments API.
 * Matches the Earnings page behavior:
 * - payoutStatus === undefined with a payment record → "paid"
 * - Negative amountInCents → "reversal"
 */
function getEffectiveStatus(
  paymentStatus: ShiftPaymentStatus | undefined,
  isVerified: boolean
): EffectiveStatus {
  // When we have a payment record from the API
  if (paymentStatus && !paymentStatus.isUnknown) {
    // Reversal detection matches Earnings page (negative amountInCents)
    if (isReversal(paymentStatus)) {
      return "reversal";
    }

    // Match Earnings page: payoutStatus === undefined treated as "paid"
    if (paymentStatus.payoutStatus === undefined) {
      return "paid";
    }

    return paymentStatus.payoutStatus;
  }

  // Without payment API data, show pending-verification status
  return isVerified ? "pending" : "pending-verification";
}

/**
 * Gets the effective payout method, falling back to shift data if needed.
 */
function getEffectiveMethod(
  paymentStatus: ShiftPaymentStatus | undefined,
  isInstantPay: boolean
): PayoutMethod | undefined {
  if (paymentStatus?.payoutMethod) {
    return paymentStatus.payoutMethod;
  }

  return isInstantPay ? "instant" : "standard";
}

function getPillText(status: EffectiveStatus): string {
  switch (status) {
    case "paid": {
      return "Paid";
    }

    case "pending": {
      return "In transit";
    }

    case "failed": {
      return "Failed payout";
    }

    case "reversal": {
      return "Reversal";
    }

    case "pending-verification": {
      return "Pending";
    }

    default: {
      return status satisfies never;
    }
  }
}

function getPillBackgroundColor(theme: Theme, status: EffectiveStatus): string {
  // Use grey as safe fallback if intent palette is unavailable
  // eslint-disable-next-line prefer-destructuring
  const fallbackBackground = theme.palette.grey[100];

  switch (status) {
    case "paid": {
      return theme.palette.intent?.success.background ?? fallbackBackground;
    }

    case "pending": {
      return theme.palette.intent?.neutral.background ?? fallbackBackground;
    }

    case "pending-verification":
    case "reversal": {
      return theme.palette.intent?.warning.background ?? fallbackBackground;
    }

    case "failed": {
      return theme.palette.intent?.error.background ?? fallbackBackground;
    }

    default: {
      return (status satisfies never) ?? fallbackBackground;
    }
  }
}

function getPillTextColor(theme: Theme, status: EffectiveStatus): string {
  // Use primary text as safe fallback if intent palette is unavailable
  const fallbackText = theme.palette.text.primary;

  switch (status) {
    case "paid": {
      return theme.palette.intent?.success.text ?? fallbackText;
    }

    case "pending": {
      return theme.palette.intent?.neutral.text ?? fallbackText;
    }

    case "pending-verification":
    case "reversal": {
      return theme.palette.intent?.warning.text ?? fallbackText;
    }

    case "failed": {
      return theme.palette.intent?.error.text ?? fallbackText;
    }

    default: {
      return (status satisfies never) ?? fallbackText;
    }
  }
}

function getIcon(
  theme: Theme,
  status: EffectiveStatus,
  payoutMethod: PayoutMethod | undefined
):
  | {
      type: IconType;
      color?: string;
      style?: SxProps<Theme>;
    }
  | undefined {
  const style: SxProps<Theme> = {
    width: theme.spacing(4),
    height: theme.spacing(4),
    marginLeft: theme.spacing(1),
  };

  // Matches Earnings page getPayoutStatusIcon logic
  if (status === "paid" && payoutMethod === "instant") {
    return {
      type: "bolt-filled",
      color: theme.palette.intent?.success.text,
      style,
    };
  }

  if (status === "failed") {
    return {
      type: "triangle-exclamation",
      color: theme.palette.intent?.error.text,
      style,
    };
  }

  // No icon for paid (standard), pending, pending-verification, or reversal
  return undefined;
}
